import type {
  LfaAccountServiceV2FieldsFragment,
  LfaServicesV2FieldsFragment,
} from '~/graphql/types';
import type { OfficeChangeFragment } from './types';

type ResolvedService = Omit<LfaAccountServiceV2FieldsFragment, '__typename'>;
type CalendarRef = ResolvedService['calendars'][number];

const applyChange = (
  services: Array<ResolvedService>,
  change: OfficeChangeFragment,
): Array<ResolvedService> => {
  switch (change.__typename) {
    case 'LFA_Office_Change_Add_V2': {
      if (services.some(s => s.id === change.serviceId)) return services;
      return [
        ...services,
        {
          id: change.serviceId,
          locations: change.addLocations,
          criteria: change.addCriteria,
          calendars: change.addCalendars,
        },
      ];
    }
    case 'LFA_Office_Change_Remove_V2':
      return services.filter(s => s.id !== change.serviceId);
    case 'LFA_Office_Change_Mutate_V2':
      return services.map(s => {
        if (s.id !== change.serviceId) return s;

        const locations = change.mutateLocations ?? s.locations;

        const removeCriteriaSet = new Set(change.removeCriteria ?? []);
        const criteria = [
          ...s.criteria.filter(c => !removeCriteriaSet.has(c.id)),
          ...(change.mutateAddCriteria ?? []),
        ];

        const removeCalendarSet = new Set(change.removeCalendarIds ?? []);
        const baseCals = s.calendars.filter(
          c => !removeCalendarSet.has(c.calendarId),
        );
        const addCals = change.mutateAddCalendars ?? [];
        const addCalMap = new Map(addCals.map(c => [c.calendarId, c]));
        const baseCalIds = new Set(baseCals.map(c => c.calendarId));
        const calendars: Array<CalendarRef> = [
          ...baseCals.map(c => addCalMap.get(c.calendarId) ?? c),
          ...addCals.filter(c => !baseCalIds.has(c.calendarId)),
        ];

        return { id: s.id, locations, criteria, calendars };
      });
  }
};

const resolveServicesForOffice = ({
  accountServices,
  officeChangesets,
  officeId,
}: {
  accountServices: LfaServicesV2FieldsFragment['accountServices'];
  officeChangesets: LfaServicesV2FieldsFragment['officeChangesets'];
  officeId: string;
}): Array<ResolvedService> => {
  const base: Array<ResolvedService> = accountServices.map(s => ({
    id: s.id,
    locations: [...s.locations],
    criteria: [...s.criteria],
    calendars: [...s.calendars],
  }));

  const entry = officeChangesets.find(e => e.officeId === officeId);
  if (!entry) return base;

  return entry.changeset.service.reduce(applyChange, base);
};

export default resolveServicesForOffice;
